import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/footer/Footer.jsx';

const LOGO_URL =
  'https://res.cloudinary.com/deagvpc2p/image/upload/f_webp,q_auto/v1770486714/Logotipo_Fondo_bgk6ah.png';

const QUESTIONS = [
  {
    question: '¿Hoy tu negocio tiene una web propia?',
    hint: 'No cuenta solo estar en internet: importa si tenés un lugar que realmente te represente.',
    options: [
      ['Sí, y genera consultas', 20],
      ['Sí, pero casi nadie entra', 10],
      ['Solo uso redes sociales', 5],
      ['No tengo presencia digital', 0]
    ]
  },
  {
    question: '¿Qué pasa cuando alguien busca tu negocio en Google?',
    hint: 'Pensá en la información, las reseñas y la impresión que se lleva una persona nueva.',
    options: [
      ['Aparezco bien y se entiende todo', 20],
      ['Aparezco, pero está incompleto', 11],
      ['Aparecen mis redes solamente', 6],
      ['No sé o no aparezco', 0]
    ]
  },
  {
    question: '¿Tus redes convierten seguidores en consultas?',
    hint: 'Publicar no siempre significa vender. Queremos saber si existe un camino claro al contacto.',
    options: [
      ['Sí, recibo consultas seguido', 20],
      ['A veces, pero es irregular', 12],
      ['Tengo interacción, pocas consultas', 7],
      ['Casi nunca publico', 1]
    ]
  },
  {
    question: '¿Tu marca se ve coherente en todos lados?',
    hint: 'Logo, colores, mensajes y calidad visual deberían contar la misma historia.',
    options: [
      ['Sí, está bien definida', 20],
      ['Más o menos, falta ordenar', 12],
      ['Cada canal se ve diferente', 5],
      ['Todavía no tengo una identidad', 0]
    ]
  },
  {
    question: '¿Podés medir de dónde llegan tus clientes?',
    hint: 'Sin datos es difícil saber qué acción funciona y dónde conviene invertir.',
    options: [
      ['Sí, mido y tomo decisiones', 20],
      ['Miro algunas métricas', 12],
      ['Me guío por intuición', 5],
      ['Nunca lo medí', 0]
    ]
  }
];

const AREAS = [
  'Crear una web clara y enfocada en consultas',
  'Optimizar cómo aparece tu negocio en Google',
  'Convertir tus redes en un canal de ventas',
  'Ordenar y profesionalizar tu identidad visual',
  'Medir resultados para invertir mejor'
];

function DiagnosticPage() {
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);

  useEffect(() => {
    document.title = 'Diagnóstico digital | CodingFlash';
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const completed = answers.length === QUESTIONS.length;
  const score = useMemo(
    () => answers.reduce((total, answer) => total + answer.value, 0),
    [answers]
  );

  const result = useMemo(() => {
    if (score >= 76) {
      return {
        title: 'Tenés una buena base para escalar.',
        copy: 'Tu presencia digital ya trabaja a favor del negocio. El siguiente salto está en optimizar la conversión, medir mejor y acelerar lo que funciona.'
      };
    }
    if (score >= 46) {
      return {
        title: 'Hay potencial, pero se están escapando oportunidades.',
        copy: 'Ya hiciste parte del camino. Con una estrategia más coherente podrías transformar visitas y seguidores en consultas reales.'
      };
    }
    return {
      title: 'Tu negocio necesita una base digital más sólida.',
      copy: 'Hoy dependés demasiado del boca a boca o de acciones aisladas. Ordenar lo esencial puede producir un cambio visible rápidamente.'
    };
  }, [score]);

  const priorities = useMemo(
    () =>
      answers
        .map((answer, index) => ({ label: AREAS[index], value: answer.value }))
        .sort((a, b) => a.value - b.value)
        .slice(0, 3),
    [answers]
  );

  const selectAnswer = (optionIndex, value) => {
    const nextAnswers = answers.slice(0, step);
    nextAnswers[step] = { optionIndex, value };
    setAnswers(nextAnswers);
    if (step < QUESTIONS.length - 1) setStep(current => current + 1);
  };

  const restart = () => {
    setStarted(true);
    setStep(0);
    setAnswers([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <header className='border-b border-white/10 bg-black'>
        <nav className='mx-auto flex h-20 w-full max-w-[1400px] items-center justify-between px-4 sm:px-6 lg:px-10'>
          <Link to='/' className='flex items-center gap-3'>
            <img src={LOGO_URL} alt='' className='h-9 w-9 rounded-full object-cover' />
            <span className='font-display text-lg font-bold uppercase tracking-[0.09em] text-primary'>
              CodingFlash
            </span>
          </Link>
          <Link
            to='/'
            className='text-xs font-bold uppercase tracking-[0.15em] text-primary/55 transition-colors hover:text-primary'
          >
            ← Volver al inicio
          </Link>
        </nav>
      </header>

      <main className='min-h-[calc(100vh-5rem)] bg-[radial-gradient(circle_at_75%_25%,rgba(249,115,22,0.10),transparent_32%),#090909] px-4 py-14 text-primary sm:px-6 lg:px-10 lg:py-20'>
        {!started && (
          <section className='mx-auto grid min-h-[620px] w-full max-w-[1400px] items-center gap-14 lg:grid-cols-[1.25fr_0.75fr]'>
            <div>
              <span className='mb-6 block text-[11px] font-bold uppercase tracking-[0.35em] text-orange-500'>
                // TU NEGOCIO, BAJO LA LUPA
              </span>
              <h1 className='title-readable max-w-5xl font-display text-5xl font-bold uppercase leading-[0.9] sm:text-7xl lg:text-8xl'>
                ¿Tu presencia digital <span className='text-orange-500'>vende</span> o solo existe?
              </h1>
              <p className='mt-8 max-w-2xl text-base leading-relaxed text-primary/55 sm:text-lg'>
                Respondé cinco preguntas. En menos de dos minutos vas a saber dónde
                estás perdiendo oportunidades y cuál debería ser tu próximo paso.
              </p>
              <button
                type='button'
                onClick={() => setStarted(true)}
                className='mt-9 inline-flex min-h-14 items-center gap-9 bg-orange-500 px-7 text-sm font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-orange-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white'
              >
                Hacer mi diagnóstico <span className='text-xl'>→</span>
              </button>
              <div className='mt-6 flex flex-wrap gap-3 text-xs text-primary/35'>
                <span>5 preguntas</span><span>·</span>
                <span>Resultado inmediato</span><span>·</span>
                <span>Sin compromiso</span>
              </div>
            </div>

            <div className='relative mx-auto hidden aspect-square w-full max-w-[420px] border border-white/15 bg-white/[0.025] lg:block'>
              <div className='absolute inset-[14%] rounded-full border border-white/10'>
                <div className='absolute inset-[18%] rounded-full border border-white/10'>
                  <div className='absolute inset-[24%] grid place-items-center rounded-full bg-orange-500 font-bold text-white shadow-[0_0_45px_rgba(249,115,22,0.35)]'>
                    CF
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {started && !completed && (
          <section className='mx-auto min-h-[620px] w-full max-w-4xl py-8'>
            <div className='flex items-center gap-5'>
              <span className='whitespace-nowrap text-[11px] font-bold uppercase tracking-[0.2em] text-orange-500'>
                Pregunta {step + 1} de {QUESTIONS.length}
              </span>
              <div className='h-px flex-1 bg-white/10'>
                <div
                  className='h-full bg-orange-500 transition-all duration-300'
                  style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
                />
              </div>
            </div>

            <h1 className='title-readable mt-16 font-display text-4xl font-bold leading-tight sm:text-6xl'>
              {QUESTIONS[step].question}
            </h1>
            <p className='mt-4 max-w-2xl text-base leading-relaxed text-primary/50'>
              {QUESTIONS[step].hint}
            </p>

            <div className='mt-10 grid gap-3 sm:grid-cols-2'>
              {QUESTIONS[step].options.map(([label, value], optionIndex) => (
                <button
                  key={label}
                  type='button'
                  onClick={() => selectAnswer(optionIndex, value)}
                  className='min-h-20 border border-white/10 bg-white/[0.025] p-5 text-left text-base font-medium text-primary/75 transition-colors hover:border-orange-500 hover:bg-orange-500/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500'
                >
                  {label}
                </button>
              ))}
            </div>

            {step > 0 && (
              <button
                type='button'
                onClick={() => {
                  setStep(current => current - 1);
                  setAnswers(current => current.slice(0, -1));
                }}
                className='mt-8 text-sm text-primary/40 transition-colors hover:text-primary'
              >
                ← Anterior
              </button>
            )}
          </section>
        )}

        {completed && (
          <section className='mx-auto min-h-[620px] w-full max-w-5xl py-8'>
            <span className='text-[11px] font-bold uppercase tracking-[0.3em] text-orange-500'>
              // TU RESULTADO
            </span>
            <div className='mt-10 grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:items-center'>
              <div>
                <div className='font-display text-8xl font-bold leading-none tracking-[-0.08em] text-orange-500'>
                  {score}<span className='ml-2 text-lg tracking-normal text-primary/30'>/100</span>
                </div>
                <h1 className='title-readable mt-7 font-display text-3xl font-bold leading-tight sm:text-4xl'>
                  {result.title}
                </h1>
                <p className='mt-5 leading-relaxed text-primary/55'>{result.copy}</p>
              </div>

              <div className='border-t border-white/10 pt-8 lg:border-l lg:border-t-0 lg:pl-12 lg:pt-0'>
                <span className='text-[11px] font-bold uppercase tracking-[0.2em] text-primary/30'>
                  Tus próximos pasos
                </span>
                <ol className='mt-5'>
                  {priorities.map((priority, index) => (
                    <li key={priority.label} className='flex gap-5 border-t border-white/10 py-5 text-base'>
                      <span className='text-xs font-bold text-orange-500'>0{index + 1}</span>
                      <span>{priority.label}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            <div className='mt-10 flex flex-col items-start gap-5 sm:flex-row sm:items-center'>
              <Link
                to='/#contacto'
                className='inline-flex min-h-14 items-center gap-8 bg-orange-500 px-7 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-orange-400'
              >
                Quiero mejorar mi negocio <span className='text-xl'>→</span>
              </Link>
              <button
                type='button'
                onClick={restart}
                className='text-sm text-primary/40 transition-colors hover:text-primary'
              >
                Repetir diagnóstico
              </button>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}

export default DiagnosticPage;
